import {
  certChainToBase64,
  complete,
  inspect,
  prepare,
  signedAttrsFromResponse,
  type InspectField,
} from "./api";
import { getCertificate, initCertUi, type CertUiElements } from "./crypto/certState";
import { signAttributes } from "./crypto/sign";
import { STAMPIT_FIELD_NAME } from "./pdf/constants";
import { attachBoxPicker, drawReadOnlyBox, type PdfBox } from "./pdf/boxpicker";
import { loadPdf, renderPage } from "./pdf/render";
import {
  attachSignaturePad,
  clearSavedSignature,
  drawBlobFitted,
  restoreSavedSignature,
  saveSignature,
} from "./signature";
import { initDebugPanel } from "./debugpanel";
import { deliverSigned, initMappe } from "./mappe/mappe";
import type { PDFDocumentProxy } from "pdfjs-dist";

void initDebugPanel();

const form = document.getElementById("sign-form") as HTMLFormElement;
const statusEl = document.getElementById("status") as HTMLParagraphElement;
const modeInfo = document.getElementById("mode-info") as HTMLDivElement;
const loadingIndicator = document.getElementById("loading-indicator") as HTMLDivElement;
const pageControl = document.getElementById("page-control") as HTMLDivElement;
const prevPageBtn = document.getElementById("prevPageBtn") as HTMLButtonElement;
const nextPageBtn = document.getElementById("nextPageBtn") as HTMLButtonElement;
const pageIndicator = document.getElementById("pageIndicator") as HTMLSpanElement;
const canvas = document.getElementById("page-canvas") as HTMLCanvasElement;
const signaturePadCanvas = document.getElementById("signature-pad") as HTMLCanvasElement;
const clearSignatureBtn = document.getElementById("clearSignatureBtn") as HTMLButtonElement;
const rememberSignatureInput = document.getElementById("rememberSignature") as HTMLInputElement;
const stampImageLabel = document.getElementById("stampImageLabel") as HTMLLabelElement;
const drawHint = document.getElementById("draw-hint") as HTMLParagraphElement;
const stampImageInput = document.getElementById("stampImage") as HTMLInputElement;

signaturePadCanvas.width = 400;
signaturePadCanvas.height = 150;

// Once a signature is actually in place -- drawn, uploaded, or restored from
// browser storage -- the upload control and "...oder zeichnen" hint are just
// clutter; "Unterschrift-Bild löschen" is the way back to choosing a
// different one.
function refreshSignatureControlsVisibility(): void {
  const hasSignature = !signaturePad.isEmpty() || !!stampImageInput.files?.length;
  stampImageLabel.hidden = hasSignature;
  drawHint.hidden = hasSignature;
}

const signaturePad = attachSignaturePad(signaturePadCanvas, refreshSignatureControlsVisibility);

// Uploaded images render immediately in the same field as a drawn/restored
// signature, purely so the user sees what they picked -- but submission
// still prefers the pristine uploaded bytes over the pad's re-rasterized
// canvas (letterboxed to fit the pad's fixed aspect ratio), so markEmpty()
// keeps isEmpty() true unless the user actually draws on top afterwards.
stampImageInput.addEventListener("change", async () => {
  refreshSignatureControlsVisibility();
  const file = stampImageInput.files?.[0];
  if (!file) return;
  try {
    await drawBlobFitted(signaturePadCanvas, file);
    signaturePad.markEmpty();
  } catch {
    setStatus("Bild konnte nicht als Vorschau geladen werden.");
  }
});

clearSignatureBtn.addEventListener("click", async () => {
  signaturePad.clear();
  await clearSavedSignature();
  rememberSignatureInput.checked = false;
  stampImageInput.value = "";
  refreshSignatureControlsVisibility();
});

void restoreSavedSignature(signaturePadCanvas).then((restored) => {
  if (restored) {
    signaturePad.markNonEmpty();
    rememberSignatureInput.checked = true;
  }
  // Signals to e2e tests that the (async) restore attempt has finished.
  signaturePadCanvas.dataset.restored = "true";
});

const certUi: CertUiElements = {
  certStatus: document.getElementById("cert-status") as HTMLElement,
  passkeyStatus: document.getElementById("passkey-status") as HTMLElement,
  p12FileLabel: document.getElementById("p12-file-label") as HTMLElement,
  passphraseLabel: document.getElementById("passphrase-label") as HTMLElement,
  p12FileInput: document.getElementById("p12File") as HTMLInputElement,
  passphraseInput: document.getElementById("passphrase") as HTMLInputElement,
  rememberP12: document.getElementById("rememberP12") as HTMLInputElement,
  rememberP12Label: document.getElementById("rememberP12Label") as HTMLElement,
  setupPasskey: document.getElementById("setupPasskey") as HTMLInputElement,
  setupPasskeyLabel: document.getElementById("setupPasskeyLabel") as HTMLElement,
  signBtn: document.getElementById("signBtn") as HTMLButtonElement,
  forgetBtn: document.getElementById("forgetCertBtn") as HTMLButtonElement,
};
void initCertUi(certUi);

let pdfDocument: PDFDocumentProxy | null = null;
let mode: "frei" | "stampit" = "frei";
let stampitField: InspectField | null = null;
let selectedBox: PdfBox | null = null;
let detachPicker: (() => void) | null = null;
let currentPageIndex = 0;

function setStatus(text: string): void {
  statusEl.textContent = text;
}

function setLoading(active: boolean): void {
  loadingIndicator.hidden = !active;
}

async function renderCurrentPage(pageIndex: number): Promise<void> {
  if (!pdfDocument) return;
  currentPageIndex = pageIndex;
  detachPicker?.();
  detachPicker = null;
  delete canvas.dataset.ready;

  const { viewport } = await renderPage(pdfDocument, pageIndex, canvas);

  if (mode === "stampit" && stampitField) {
    drawReadOnlyBox(canvas, viewport, stampitField.box);
    selectedBox = stampitField.box;
  } else {
    selectedBox = null;
    detachPicker = attachBoxPicker({
      canvas,
      viewport,
      onChange: (box) => {
        selectedBox = box;
      },
    });
  }

  pageIndicator.textContent = `Seite ${pageIndex + 1} / ${pdfDocument.numPages}`;
  prevPageBtn.disabled = pageIndex <= 0;
  nextPageBtn.disabled = pageIndex >= pdfDocument.numPages - 1;

  // Signals to e2e tests that the box-picker's pointer listeners (or the
  // read-only box) are now attached, since canvas.width alone is set earlier.
  canvas.dataset.ready = "true";
}

async function onPdfSelected(pdfFile: File): Promise<void> {
  const buffer = await pdfFile.arrayBuffer();
  pdfDocument = await loadPdf(buffer.slice(0));

  const fields = await inspect(pdfFile);
  const emptyStampitField = fields.find(
    (f) => f.field_name === STAMPIT_FIELD_NAME && !f.filled,
  );

  if (emptyStampitField) {
    mode = "stampit";
    stampitField = emptyStampitField;
    pageControl.hidden = true;
    modeInfo.textContent = "Unterschriftsfeld gefunden - Position wird übernommen.";
    await renderCurrentPage(emptyStampitField.page);
  } else {
    mode = "frei";
    stampitField = null;
    pageControl.hidden = false;
    modeInfo.textContent = "Bereich für die Unterschrift auf der Seite ziehen.";
    await renderCurrentPage(0);
  }
}

async function handlePdfFile(file: File): Promise<void> {
  setLoading(true);
  try {
    await onPdfSelected(file);
  } catch (err) {
    setStatus(`Fehler beim Laden des PDFs: ${err instanceof Error ? err.message : String(err)}`);
  } finally {
    setLoading(false);
  }
}

const pdfFileInput = document.getElementById("pdfFile") as HTMLInputElement;
const pdfDropZone = document.getElementById("pdf-drop-zone") as HTMLDivElement;

pdfFileInput.addEventListener("change", async (event) => {
  const file = (event.target as HTMLInputElement).files?.[0];
  if (!file) return;
  await handlePdfFile(file);
});

["dragenter", "dragover"].forEach((eventName) => {
  pdfDropZone.addEventListener(eventName, (event) => {
    event.preventDefault();
    pdfDropZone.classList.add("drag-over");
  });
});

["dragleave", "drop"].forEach((eventName) => {
  pdfDropZone.addEventListener(eventName, (event) => {
    event.preventDefault();
    pdfDropZone.classList.remove("drag-over");
  });
});

pdfDropZone.addEventListener("drop", async (event) => {
  const file = event.dataTransfer?.files?.[0];
  if (!file) return;
  if (file.type !== "application/pdf") {
    setStatus("Bitte eine PDF-Datei ablegen.");
    return;
  }
  await setPdfFile(file);
});

// Reflect the file in the <input> itself so it's included in the form
// submission the same way a manually picked file would be. Used for dropped
// files and for the PDFs of the "Unterschriften-Mappe".
async function setPdfFile(file: File): Promise<void> {
  const transfer = new DataTransfer();
  transfer.items.add(file);
  pdfFileInput.files = transfer.files;
  await handlePdfFile(file);
}

// Unloads the current PDF (used once a Mappe document has been signed and moved).
function clearPdf(): void {
  detachPicker?.();
  detachPicker = null;
  pdfDocument = null;
  stampitField = null;
  selectedBox = null;
  pdfFileInput.value = "";
  canvas.width = 0;
  canvas.height = 0;
  delete canvas.dataset.ready;
  pageControl.hidden = true;
  pageIndicator.textContent = "";
  modeInfo.textContent = "";
}

void initMappe({ openPdf: setPdfFile, clearPdf, setStatus, setLoading });

prevPageBtn.addEventListener("click", async () => {
  if (currentPageIndex <= 0) return;
  setLoading(true);
  try {
    await renderCurrentPage(currentPageIndex - 1);
  } finally {
    setLoading(false);
  }
});

nextPageBtn.addEventListener("click", async () => {
  if (!pdfDocument || currentPageIndex >= pdfDocument.numPages - 1) return;
  setLoading(true);
  try {
    await renderCurrentPage(currentPageIndex + 1);
  } finally {
    setLoading(false);
  }
});

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

form.addEventListener("submit", async (event) => {
  event.preventDefault();

  const pdfInput = document.getElementById("pdfFile") as HTMLInputElement;

  const pdfFile = pdfInput.files?.[0];
  if (!pdfFile) {
    setStatus("Bitte PDF auswählen.");
    return;
  }
  if (!selectedBox) {
    setStatus(
      mode === "frei" ? "Bitte zuerst eine Box auf der Seite ziehen." : "Feldposition nicht ermittelt.",
    );
    return;
  }

  const signBtn = certUi.signBtn;
  signBtn.disabled = true;
  try {
    setStatus("Zertifikat wird geladen...");
    const { privateKey, certChainDer } = await getCertificate(certUi);
    setStatus("Signatur wird vorbereitet...");

    const box = selectedBox;
    let stampImageFile: File | Blob | undefined = stampImageInput.files?.[0];
    if (!signaturePad.isEmpty()) {
      stampImageFile = await signaturePad.toBlob();
    }
    // Remembers whichever image actually ends up used -- drawn or uploaded --
    // so the checkbox does what it says regardless of which path the user took.
    if (rememberSignatureInput.checked && stampImageFile) {
      await saveSignature(stampImageFile);
    }

    const prepareResponse = await prepare(
      pdfFile,
      {
        mode,
        field_name: "Signatur",
        page: mode === "stampit" ? (stampitField?.page ?? 0) : currentPageIndex,
        box,
        cert_chain: certChainToBase64(certChainDer),
        reason: "Freigabe",
        with_timestamp: true,
      },
      stampImageFile,
    );

    setStatus("Signiere...");
    const signature = await signAttributes(
      privateKey,
      signedAttrsFromResponse(prepareResponse),
    );

    setStatus("Fertigstellen...");
    const { blob, filename } = await complete(prepareResponse.session_id, signature);

    // In "Unterschriften-Mappe" mode the signed PDF is stored in ownCloud
    // instead; on any problem there it falls back to the download below.
    const delivery = await deliverSigned(blob, filename);
    if (delivery.handled) {
      setStatus(delivery.note ?? "Fertig.");
    } else {
      triggerDownload(blob, filename);
      setStatus(`Fertig - Download gestartet.${delivery.note ? ` ${delivery.note}` : ""}`);
    }
  } catch (err) {
    setStatus(`Fehler: ${err instanceof Error ? err.message : String(err)}`);
  } finally {
    // The label itself is owned by certState's render(), which may have
    // changed it (e.g. after falling back from a passkey to the passphrase).
    signBtn.disabled = false;
  }
});
